import { useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Target, Zap, BarChart3, Users, X, Lightbulb, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/accompagnement-rive-sud.jpg";

const AccompagnementRiveSudPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Agence 80/20 - Accompagnement marketing Rive-Sud",
    "description": "Agence 80/20 accompagne des entrepreneurs et PME sur la Rive-Sud qui souhaitent structurer leur marketing afin de générer des leads qualifiés grâce au contenu stratégique et à la publicité payante.",
    "url": "https://agence8020.com/accompagnement-marketing-rive-sud",
    "areaServed": {
      "@type": "Place",
      "name": "Rive-Sud de Montréal",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Québec"
      }
    },
    "serviceType": ["Marketing digital", "Génération de leads", "Création de contenu", "Publicité payante"],
    "provider": {
      "@type": "Organization",
      "name": "Agence 80/20",
      "url": "https://agence8020.com"
    }
  };

  return (
    <>
      <SEOHead
        title="Accompagnement marketing sur la Rive-Sud | Génération de leads – Agence 80/20"
        description="Agence 80/20 offre un accompagnement marketing sur la Rive-Sud pour entrepreneurs et PME qui veulent générer des leads grâce au contenu stratégique et à la publicité payante."
        canonical="/accompagnement-marketing-rive-sud"
        schemaJson={jsonLd}
      />

      <main className="min-h-screen bg-background">
        <Navbar />
        
        {/* Hero Section */}
        <header className="pt-32 pb-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={heroImage} 
              alt="Accompagnement marketing sur la Rive-Sud" 
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="text-foreground">Accompagnement marketing sur la </span>
                <span className="text-primary">Rive-Sud</span>
                <span className="text-foreground"> pour générer des leads</span>
              </h1>
              <p className="text-muted-foreground text-lg md:text-xl mb-4 max-w-3xl">
                Agence 80/20 accompagne des entrepreneurs et PME sur la Rive-Sud qui souhaitent structurer leur marketing afin de générer des leads qualifiés grâce au contenu stratégique et à la publicité payante.
              </p>
              <p className="text-muted-foreground text-lg mb-8 max-w-3xl">
                <span className="text-foreground font-medium">L'objectif est simple : transformer la visibilité en opportunités d'affaires concrètes.</span>
              </p>
              <Link to="/accompagnement-marketing-contenu">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Réserver un appel stratégique
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </header>

        {/* Section: Marché Rive-Sud */}
        <section className="py-20 bg-card/50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Un accompagnement marketing adapté aux entreprises de la Rive-Sud
              </h2>
              <p className="text-muted-foreground text-lg mb-6">
                La Rive-Sud regroupe de nombreuses entreprises en croissance, dans un environnement de plus en plus compétitif.
              </p>
              <p className="text-muted-foreground text-lg mb-6">
                Pour se démarquer sur la Rive-Sud, il ne suffit plus d'être présent en ligne.<br />
                <span className="text-foreground font-medium">Il faut une stratégie marketing claire, cohérente et orientée résultats.</span>
              </p>
              <p className="text-foreground text-lg font-medium">
                C'est exactement ce que propose Agence 80/20.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section: Enjeux */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Les enjeux marketing fréquents des entreprises sur la Rive-Sud
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Beaucoup d'entreprises sur la Rive-Sud rencontrent les mêmes problèmes :
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: X, text: "Du contenu publié sans objectif précis" },
                  { icon: X, text: "Des publicités payantes peu rentables" },
                  { icon: X, text: "Peu de cohérence entre l'organique et le payant" },
                  { icon: X, text: "Des leads peu qualifiés" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-card rounded-lg border border-border">
                    <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-destructive" />
                    </div>
                    <span className="text-foreground">{item.text}</span>
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground text-lg mt-8">
                Sans structure, les efforts marketing donnent peu de résultats mesurables.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section: Approche */}
        <section className="py-20 bg-card/50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-12 text-foreground">
                L'approche Agence 80/20 sur la Rive-Sud
              </h2>
              
              <div className="space-y-12">
                {/* Étape 1 */}
                <div className="flex gap-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-3 text-foreground">
                      Clarifier le positionnement
                    </h3>
                    <p className="text-muted-foreground mb-4">On définit clairement :</p>
                    <ul className="space-y-2">
                      {["Votre client idéal sur la Rive-Sud", "Votre message principal", "Votre avantage concurrentiel"].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-foreground">
                          <Check className="w-4 h-4 text-primary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-muted-foreground mt-4">
                      Cette étape est essentielle pour bâtir une stratégie efficace.
                    </p>
                  </div>
                </div>

                {/* Étape 2 */}
                <div className="flex gap-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-3 text-foreground">
                      Créer du contenu stratégique
                    </h3>
                    <p className="text-muted-foreground mb-4">Le contenu est conçu pour :</p>
                    <ul className="space-y-2">
                      {["Attirer l'attention", "Éduquer l'audience", "Créer de la confiance", "Préparer la conversion"].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-foreground">
                          <Check className="w-4 h-4 text-primary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-muted-foreground mt-4">
                      Chaque contenu joue un rôle précis dans le parcours client.
                    </p>
                  </div>
                </div>

                {/* Étape 3 */}
                <div className="flex gap-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-3 text-foreground">
                      Accélérer avec la publicité payante
                    </h3>
                    <p className="text-muted-foreground mb-4">La publicité payante permet de :</p>
                    <ul className="space-y-2">
                      {["Toucher rapidement votre marché sur la Rive-Sud", "Tester et optimiser les messages", "Générer des leads plus rapidement"].map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-foreground">
                          <Check className="w-4 h-4 text-primary" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-muted-foreground mt-4">
                      Le contenu nourrit la publicité, et la publicité amplifie le contenu.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section: Pour qui */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8 text-foreground">
                À qui s'adresse cet accompagnement marketing sur la Rive-Sud
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="font-heading text-lg font-bold mb-4 text-foreground flex items-center gap-2">
                    <Check className="w-5 h-5 text-primary" />
                    Cet accompagnement est conçu pour :
                  </h3>
                  <ul className="space-y-3">
                    {["Entrepreneurs sur la Rive-Sud", "PME de la Rive-Sud", "Marques personnelles", "Entreprises B2B"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-muted-foreground">
                        <Users className="w-4 h-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-card p-6 rounded-xl border border-border">
                  <h3 className="font-heading text-lg font-bold mb-4 text-foreground flex items-center gap-2">
                    <X className="w-5 h-5 text-destructive" />
                    Ce n'est pas pour :
                  </h3>
                  <ul className="space-y-3">
                    {["Ceux qui veulent des résultats sans stratégie", "Ceux qui cherchent uniquement des publications", "Ceux qui veulent le prix le plus bas"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-muted-foreground">
                        <X className="w-4 h-4 text-muted-foreground" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section: Pourquoi nous */}
        <section className="py-20 bg-card/50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8 text-foreground">
                Pourquoi choisir Agence 80/20 sur la Rive-Sud
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Choisir Agence 80/20, c'est travailler avec une agence qui :
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { icon: BarChart3, text: "Comprend la réalité des entreprises québécoises" },
                  { icon: Target, text: "Adapte la stratégie au marché local" },
                  { icon: Lightbulb, text: "Met la structure avant l'exécution" },
                  { icon: TrendingUp, text: "Se concentre sur la génération de leads" }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-background rounded-lg border border-border">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-foreground">{item.text}</span>
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground text-lg mt-8">
                L'accompagnement est pensé comme un partenariat de croissance.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Section: Processus */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-12 text-foreground">
                Comment débute l'accompagnement marketing
              </h2>
              
              <div className="space-y-6">
                {[
                  { num: "1", title: "Appel stratégique" },
                  { num: "2", title: "Analyse de la situation actuelle" },
                  { num: "3", title: "Définition de la stratégie" },
                  { num: "4", title: "Mise en place du contenu et de la publicité" },
                  { num: "5", title: "Optimisation continue" }
                ].map((step, index) => (
                  <div key={index} className="flex items-center gap-6">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-foreground font-bold">{step.num}</span>
                    </div>
                    <span className="text-foreground text-lg">{step.title}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-muted-foreground text-lg mt-8">
                Chaque étape vise des résultats concrets et mesurables.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary/5">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-foreground">
                Prêt à générer plus de leads sur la Rive-Sud ?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Si vous êtes un entrepreneur ou une PME sur la Rive-Sud et que vous voulez structurer votre marketing pour obtenir des leads qualifiés de façon constante, cet accompagnement est conçu pour vous.
              </p>
              <Link to="/accompagnement-marketing-contenu">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Réserver un appel stratégique
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Liens internes */}
        <section className="py-16 border-t border-border">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-semibold mb-6 text-foreground">
                Découvrir nos autres services
              </h3>
              <div className="flex flex-wrap gap-4">
                <Link to="/accompagnement-marketing-quebec" className="text-primary hover:underline">
                  Accompagnement marketing Québec
                </Link>
                <span className="text-muted-foreground">•</span>
                <Link to="/accompagnement-marketing-montreal" className="text-primary hover:underline">
                  Accompagnement marketing Montréal
                </Link>
                <span className="text-muted-foreground">•</span>
                <Link to="/accompagnement-marketing-laval" className="text-primary hover:underline">
                  Accompagnement marketing Laval
                </Link>
                <span className="text-muted-foreground">•</span>
                <Link to="/accompagnement" className="text-primary hover:underline">
                  Accompagnement marketing
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default AccompagnementRiveSudPage;
